import { ErrorBoundary } from "./components";

import { createRouter, RouterProvider } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";
import { Toaster } from "sonner";
import "./index.css";
import "@/packages/utils";

import { LoggerProvider } from "@/packages/frontend-logger";
import { routeTree } from "./routeTree.gen";
import type { RouterContext } from "./router-context";
import {
  initSentry,
  initPerformanceMonitoring,
  ConvexClientProvider,
} from "./lib";
import { ErrorModal } from "@/packages/error-handling";
import {
  useErrorModalStore,
  registerErrorInterceptor,
} from "@/packages/features-shared";
import { CreditExhaustedModal } from "@/components/ai/CreditExhaustedModal";
import { useCreditExhaustedModal } from "@/components/ai/credit-exhausted-modal-store";
import { AuditLimitModal } from "@/components/billing/AuditLimitModal";
import { useAuditLimitModal } from "@/components/billing/audit-limit-modal-store";

// Intercept CREDIT_EXHAUSTED errors globally — show modal instead of toast
registerErrorInterceptor("CREDIT_EXHAUSTED", () => {
  useCreditExhaustedModal.getState().open();
});

// Intercept AUDIT_LIMIT_REACHED errors globally — show modal instead of toast
registerErrorInterceptor("AUDIT_LIMIT_REACHED", (error) => {
  const details = (error as Record<string, unknown>)?.details as
    | Record<string, unknown>
    | undefined;
  const maxAudits =
    typeof details?.maxAudits === "number" ? details.maxAudits : undefined;
  useAuditLimitModal.getState().open(maxAudits);
});

function GlobalErrorModal() {
  const { isOpen, error, closeErrorModal } = useErrorModalStore();
  if (!error) return null;
  return (
    <ErrorModal
      isOpen={isOpen}
      onClose={closeErrorModal}
      error={error instanceof Error ? error : new Error(String(error))}
    />
  );
}

// Initialize Sentry before anything else
initSentry();

// Initialize performance monitoring after Sentry (for integration)
initPerformanceMonitoring();

// Create router context with services
const routerContext: RouterContext = {
  //
};

const router = createRouter({ routeTree, context: routerContext });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

ReactDOM.createRoot(rootElement).render(
  // <React.StrictMode>
  <LoggerProvider>
    <ErrorBoundary>
      <ConvexClientProvider>
        <RouterProvider router={router} />
        <Toaster
          position="top-center"
          toastOptions={{
            classNames: {
              success:
                "!bg-white !text-[#0a0a0a] !border-[#1a7a52]/25 border-l-2 !border-l-[#1a7a52] [&>[data-icon]]:text-[#1a7a52]",
              error:
                "!bg-[#FEF2F2] !text-[#991B1B] !border-[#dc2626]/25 border-l-2 !border-l-[#dc2626] [&>[data-icon]]:!text-[#dc2626]",
            },
          }}
        />
        <GlobalErrorModal />
        <CreditExhaustedModal />
        <AuditLimitModal />
      </ConvexClientProvider>
    </ErrorBoundary>
  </LoggerProvider>,
);

console.log("Application started");
